import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import jsonData from "../../../../current-survey-numeric.json";
import { FILTER_CONFIG } from "../../../../config/filterconfig"; // Import filter config

const engagementGroups = {
  "Role Clarity": ["MQ0044", "MQ0047", "MQ0017"],
  "Resources": ["MQ0048", "MQ0051", "MQ0050"],
  "Work Outcomes": ["MQ0095", "MQ0016", "MQ0059"],
  "Feedback": ["MQ0104", "MQ0173", "MQ0054"],
};

const Heatmap1 = ({ selectedDemographic, filterCriteria }) => {
  const [chartData, setChartData] = useState([]);
  const [prevDemographic, setPrevDemographic] = useState(selectedDemographic);

  useEffect(() => {
    if (!selectedDemographic) {
      console.warn("⚠️ selectedDemographic is undefined.");
      return;
    }

    // Retrieve the predefined sort order from FILTER_CONFIG
    const demographicConfig = FILTER_CONFIG.find(
      (item) => item.jsonKey === selectedDemographic
    );
    const predefinedSortOrder = demographicConfig?.sortOrder || [];

    // Filter the data based on criteria
    const filteredData = jsonData.filter((item) =>
      Object.keys(filterCriteria).every(
        (key) =>
          filterCriteria[key]?.length === 0 ||
          filterCriteria[key]?.includes(item[key])
      )
    );

    // Get unique demographic values, replacing null/undefined with "Unknown"
    let demographicValues = [
      ...new Set(
        filteredData.map((item) => item[selectedDemographic] || "Unknown")
      ),
    ];

    // Apply predefined sorting if available
    if (predefinedSortOrder.length > 0) {
      demographicValues = demographicValues.sort((a, b) => {
        const indexA = predefinedSortOrder.indexOf(a);
        const indexB = predefinedSortOrder.indexOf(b);

        if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Alphabetical only if both are missing
        if (indexA === -1) return 1; // Push missing values to the end
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    } else {
      demographicValues = demographicValues.sort((a, b) => a.localeCompare(b));
    }

    // Ensure demographicValues is an array before using .length
    if (!Array.isArray(demographicValues) || demographicValues.length === 0) {
      setChartData([]);
      return;
    }

    // Prepare series data for each engagement group
    const seriesData = Object.keys(engagementGroups).map((group) => {
      const groupData = demographicValues.map((value) => {
        const responses = filteredData.filter(
          (item) => (item[selectedDemographic] || "Unknown") === value
        );

        // Calculate average score for the group
        const scores = responses.map(
          (response) =>
            engagementGroups[group].reduce(
              (acc, question) => acc + (parseFloat(response[question]) || 0),
              0
            ) / engagementGroups[group].length
        );

        const averageScore = scores.length
          ? scores.reduce((acc, score) => acc + score, 0) / scores.length
          : 0;

        return {
          x: value,
          y: isNaN(averageScore) ? 0 : parseFloat(averageScore.toFixed(2)),
        };
      });

      return { name: group, data: groupData };
    });

    setChartData(seriesData);
  }, [selectedDemographic, filterCriteria]);

  // Handle resizing and maintain size
  useEffect(() => {
    // Function to fix chart dimensions
    const fixChartDimensions = () => {
      const charts = document.querySelectorAll('.heatmap-1 .apexcharts-canvas');
      charts.forEach(chart => {
        chart.style.width = '100%';
        chart.style.height = '100%';
        chart.style.minHeight = '100%';
        chart.style.maxHeight = '100%';
        chart.style.position = 'relative';
        chart.style.overflow = 'hidden';
      });
    };

    // Fix on mount and window resize
    fixChartDimensions();
    window.addEventListener('resize', fixChartDimensions);
    
    // Run again after a short delay to ensure the chart has rendered
    const timeout = setTimeout(fixChartDimensions, 100);
    
    return () => {
      window.removeEventListener('resize', fixChartDimensions);
      clearTimeout(timeout);
    };
  }, []);

  // Handle demographic changes
  useEffect(() => {
    if (prevDemographic !== selectedDemographic) {
      setPrevDemographic(selectedDemographic);
      
      // Fix dimensions after demographic change
      setTimeout(() => {
        const charts = document.querySelectorAll('.heatmap-1 .apexcharts-canvas');
        charts.forEach(chart => {
          chart.style.width = '100%';
          chart.style.height = '100%';
        });
      }, 100);
    }
  }, [selectedDemographic, prevDemographic]);

  // Fix dimensions when chart data changes
  useEffect(() => {
    if (chartData.length > 0) {
      setTimeout(() => {
        const charts = document.querySelectorAll('.heatmap-1 .apexcharts-canvas');
        charts.forEach(chart => {
          chart.style.width = '100%';
          chart.style.height = '100%';
        });
      }, 100);
    }
  }, [chartData]);

  const options = {
    chart: {
      type: "heatmap",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      animations: {
        enabled: false // Disable animations
      },
      dynamicAnimation: {
        enabled: false
      },
      selection: {
        enabled: false
      },
      zoom: {
        enabled: false
      },
      width: '100%',
      height: '100%',
      redrawOnWindowResize: false, // Prevent redrawing on window resize
      redrawOnParentResize: false, // Prevent redrawing on parent resize
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        colorScale: {
          ranges: [
            { from: 0, to: 2.99, color: "#B8d4ff", name: "Very Low 0-2.99" },
            { from: 3.0, to: 3.49, color: "#8facf8", name: "Low 3-3.49" },
            { from: 3.5, to: 3.99, color: "#6686cf", name: "Medium 3.5-3.99" },
            { from: 4, to: 4.49, color: "#3c62a6", name: "High 4-4.49" },
            { from: 4.5, to: 5, color: "#004080", name: "Very High 4.5-5" },
          ],
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: ["#000"],
        fontSize: '12px', // Fixed font size
      },
      formatter: (val) => (isNaN(val) ? "0.0" : val.toFixed(1)),
    },
    xaxis: {
      categories:
        chartData.length > 0 && chartData[0].data
          ? chartData[0].data.map((d) => d.x)
          : [],
      labels: {
        style: {
          colors: "#6d6d6d",
          fontSize: "12px", // Fixed font size
        },
        trim: true, // Trim long labels
        maxHeight: 100, // Limit label height
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#6d6d6d",
          fontSize: "12px", // Fixed font size
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(1)} (Avg)`,
      },
    },
    responsive: [], // Empty array to disable responsive configs
    maintainAspectRatio: false, // Don't maintain aspect ratio
  };

  return (
    <div className="heatmap-1" style={{ 
      height: "100%", 
      width: "100%", 
      position: "relative",
      overflow: "hidden"
    }}>
      <Chart
        options={options}
        series={chartData}
        type="heatmap"
        height="100%"
        width="100%"
      />
    </div>
  );
};

export default Heatmap1;